import { cartConfig, reservesConfig, selectionlistConfig } from '../../config';
import * as types from '../constants';
import { globalErrorResponse } from '../notifications/actions';
import { AppDispatch, AppThunkAction, GetAppState } from '../index';
import {
    COPIES_LIST_FAILURE,
    COPIES_LIST_REQUEST,
    COPIES_LIST_SUCCESS,
    COPIES_LIST_USAGE_CONDITIONS_UPDATE,
    CopiesListFailure,
    CopiesListRequest,
    CopiesListSuccess,
    CopiesListUpdateUsageConditions
} from './types';
import { CopiesListSuccessPayload } from './state';
import { invalidateCartSummaryQuery } from '../../api/shopping-cart/useCartSummaryQuery';
import { invalidateSelectionListProducts } from '../../shop/selectionlists/api/useSelectionListProducts';
import { invalidateSelectionListSummary } from '../../api/selection-list/useSelectionListSummary';
import { invalidateShoppingCartProducts } from '../../api/shopping-cart/useShoppingCartProducts';
import { invalidateCartGroupedItems } from '../../api/shopping-cart/useCartGroupedItems';
import { invalidateSelectionListGroupedItems } from '../../api/selection-list/useSelectionListGroupedItems';
import { invalidateCartAggregations } from '../../shop/cart/useCartAggregations';
import { invalidateSelectionListAggregations } from '../../shop/selectionlists/useSelectionListAggregations';
import { disableListLoading, enableListLoading } from '../listLoading/actions';
import i18n from '../../../i18n';
import { http } from '../../api/axios';

const endpointCartProduct = cartConfig().endpointProducts;
const endpointSelectionlistProduct = selectionlistConfig().endpointProducts;
const endpointReserveProductCopies = reservesConfig().endpointReserveProductCopies;

const requestCopiesList = (): CopiesListRequest => {
    return { type: COPIES_LIST_REQUEST };
};
const failedCopiesList = (): CopiesListFailure => {
    return { type: COPIES_LIST_FAILURE, error: true };
};

export const copiesListUsageConditionUpdate = (productIds: string[]): CopiesListUpdateUsageConditions => {
    return { type: COPIES_LIST_USAGE_CONDITIONS_UPDATE, payload: productIds };
};

const receiveCopiesList = (resp: CopiesListSuccessPayload): CopiesListSuccess => {
    return { type: COPIES_LIST_SUCCESS, payload: resp };
};

function failCopiesList(error) {
    return (dispatch: AppDispatch) => {
        dispatch(globalErrorResponse(error));
        dispatch(failedCopiesList());
    };
}

const RESERVE_CHECKOUT_PATH = /^\/assistant\/reserve\/(.*?)\/checkout/;
const RESERVE_CHECKOUT_PATH_V2 = /^\/collectionmgmt\/reserves\/(.*?)\/checkout/;

function extractReserveListId(pathname: string) {
    const [, id] = RESERVE_CHECKOUT_PATH.exec(pathname) || [];
    return id;
}

function extractReserveListIdV2(pathname: string) {
    const [, id] = RESERVE_CHECKOUT_PATH_V2.exec(pathname) || [];
    return id;
}

export function fetchCopiesForProduct(productId: string): AppThunkAction<Promise<void>> {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        dispatch(requestCopiesList());

        const pathParams = {
            productId
        };

        // TODO refactor this quickly
        let endpoint = '';
        const pathname = getState().router.location?.pathname ?? '';
        if (pathname.startsWith(types.PATH_SELECTIONLISTS)) {
            endpoint = endpointSelectionlistProduct;
            pathParams['listId'] = getState().orderlist.id;
        } else if (pathname.match(RESERVE_CHECKOUT_PATH)) {
            pathParams['reserveListId'] = extractReserveListId(pathname);
            endpoint = endpointReserveProductCopies;
        } else if (pathname.match(RESERVE_CHECKOUT_PATH_V2)) {
            pathParams['reserveListId'] = extractReserveListIdV2(pathname);
            endpoint = endpointReserveProductCopies;
        } else if (pathname.startsWith(types.PATH_CART) || pathname.startsWith(types.PATH_CHECKOUT)) {
            endpoint = endpointCartProduct;
        } else if (pathname.startsWith(types.PATH_RESERVE) || pathname.startsWith('/collectionmgmt/reserves')) {
            pathParams['reserveListId'] = getState().reserve.activeReservelist;
            endpoint = endpointReserveProductCopies;
        } else {
            throw new Error(`invalid pathname "${pathname}!`);
        }

        return http
            .get(endpoint, { pathParams })
            .then(response => {
                dispatch(
                    receiveCopiesList({
                        productId,
                        list: response.data.copies ? response.data.copies : response.data
                    } as CopiesListSuccessPayload)
                );
            })
            .catch(error => {
                if (error.response.status === 404) {
                    // if the resource is not found (no copies in cart for this product),
                    // remove product from cart store
                    invalidateShoppingCartProducts().then();
                } else {
                    dispatch(failCopiesList(error));
                }
            });
    };
}

//TODO refactor
export function removeCopy(productId: string, copyId: string): AppThunkAction<Promise<void>> {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        const pathParams = {
            productId,
            copyId: `${copyId}`
        };

        let isCartRequest = false;
        let endpoint = '';
        const pathname = getState().router.location?.pathname ?? '';

        const listId = getState().orderlist.id ?? '';

        if (pathname.startsWith(types.PATH_SELECTIONLISTS)) {
            pathParams['listId'] = listId;
            endpoint = endpointSelectionlistProduct;
        } else if (pathname.startsWith(types.PATH_CART)) {
            endpoint = endpointCartProduct;
            isCartRequest = true;
        } else {
            throw new Error(i18n.t('notifications.removeCopy.error'));
        }

        dispatch(requestCopiesList());
        dispatch(enableListLoading());

        return http
            .delete(endpoint, {
                pathParams
            })
            .then(() => {
                dispatch(fetchCopiesForProduct(productId));

                if (isCartRequest) {
                    invalidateCartSummaryQuery().then();
                    invalidateShoppingCartProducts().then();
                    invalidateCartAggregations().then();
                    invalidateCartGroupedItems().then();
                } else {
                    invalidateSelectionListSummary(listId).then();
                    invalidateSelectionListProducts(listId).then();
                    invalidateSelectionListAggregations(listId).then();
                    invalidateSelectionListGroupedItems(listId).then();
                }
            })
            .catch(response => dispatch(failCopiesList(response)))
            .finally(() => {
                dispatch(disableListLoading());
            });
    };
}
